import { useEffect, useState } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { PlayerGetEmailSettings, PlayerUpdateEmailSettings } from "../../redux/auth-reducer/auth-service";
import { readApiMessage, setStoredEmailVerified } from "../../utils/helper/helper";
import { ALL_EMAILS_HINT, ALL_EMAILS_LABEL } from "../../utils/helper/unsubscribe";
import { PLAYER_VERIFY_MESSAGE } from "../../utils/helper/emailVerification";
import { useEmailVerified } from "../../hooks/useEmailVerification";

/**
 * Account Settings' "Email preferences": the "Emails from Managers" switch, on until the player
 * turns it off. Off stops every Manager's Broadcasting Center emails, a broadcast still waiting
 * included; the per-Manager switches on the Managers page keep their values for when it is turned
 * back on. Other emails - password reset, account notices - are not affected.
 *
 * The switch shows the server's value: it stays disabled until the first read, follows the change
 * at once, and goes back with the server's message when the change is refused or gets no reply.
 *
 * Until the player verifies their email no Manager's email reaches them, so the switch is greyed out
 * and its tooltip says why; it keeps its value. It comes back as soon as the email is verified - here
 * or in another tab (useEmailVerified).
 */
export const EmailPreferences = () => {
  const [enabled, setEnabled] = useState(null);
  const [emailVerified, setEmailVerified] = useState(null);
  const [saving, setSaving] = useState(false);
  const storedVerified = useEmailVerified();

  useEffect(() => {
    let current = true;
    PlayerGetEmailSettings().then((res) => {
      if (!current) return;
      if (res?.data?.status === true) {
        setEnabled(Boolean(res.data.broadcastEmails));
        // An API from before email verification sends no flag: nothing is locked then.
        if (typeof res.data.emailVerified === "boolean") {
          setEmailVerified(res.data.emailVerified);
          setStoredEmailVerified(res.data.emailVerified);
        }
      } else if (res) toast.error(readApiMessage(res));
    });
    return () => {
      current = false;
    };
  }, []);

  const unverified = emailVerified === false && storedVerified !== true;

  const change = async (event) => {
    const next = event.target.checked;
    setEnabled(next);
    setSaving(true);
    const res = await PlayerUpdateEmailSettings(next);
    setSaving(false);

    if (res?.data?.status === true) return;
    setEnabled(!next);
    if (res) toast.error(readApiMessage(res));
  };

  const toggle = (
    <Form.Check
      type="switch"
      id="broadcast-emails-switch"
      label={ALL_EMAILS_LABEL}
      checked={Boolean(enabled)}
      disabled={enabled === null || saving || unverified}
      onChange={change}
    />
  );

  return (
    <div className="table-transaction mb-4 email-preferences">
      <h1 className="subscription_heading">Email preferences</h1>
      {unverified ? (
        // A span, because a disabled input fires no mouse events of its own.
        <OverlayTrigger placement="top" overlay={<Tooltip>{PLAYER_VERIFY_MESSAGE}</Tooltip>}>
          <span className="email-switch-locked" tabIndex={0}>
            {toggle}
          </span>
        </OverlayTrigger>
      ) : (
        toggle
      )}
      <p className="email-preferences-hint">{ALL_EMAILS_HINT}</p>
    </div>
  );
};
